import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import get from 'lodash/get';
import qs from 'query-string';
import FilterBar from '../component/FilterBar';
import getCampaignWidgets from '../../../actions/getCampaignWidgets';
import { filter } from '../actions/actionTypes';
import ROUTES from '../../../AppRoutes/RouteConstants';
import preProcessOptions from '../utils/preProcessOptions';
import { normalize, updateFilterValues } from '../utils/queryParams';
import { fetchDataWithAppliedFilters } from '../utils/fetchData';
import { View } from '../../../components/Common/react-native-components';
import { parseQueryParams } from '../utils/queryParams';
import { sections } from '../constants/sectionConstants';
import getGeneric from '../../../actions/getGeneric';
import { debounce } from 'lodash';

const isSameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const FilterBarContainer = ({ dispatch, activeHotelId, filterSlice }) => {
  const location = useLocation();
  const history = useHistory();

  const [localFilters, setLocalFilters] = useState({});
  const [open, setOpen] = useState(false);
  const applied_filters = get(filterSlice, 'applied_filters', {});
  const showLoading = filterSlice.filtersLoading;

  const getCurrentSection = pathname => {
    if (pathname.endsWith(ROUTES.OVERVIEW)) return sections.OVERVIEW;
    if (pathname.endsWith(ROUTES.ONLINE_REVIEW)) return sections.ONLINE_REVIEW;
    if (pathname.endsWith(ROUTES.FEEDBACK)) return sections.FEEDBACK;
    if (pathname.endsWith(ROUTES.ALERTS)) return sections.ALERTS;
    if (pathname.endsWith(ROUTES.AI_INSIGHTS)) return sections.AI_INSIGHTS;
    return 'default';
  };
  const currentSection = useMemo(() => {
    return getCurrentSection(location.pathname);
  }, [location.pathname]);


  const reconcileLocalFiltersWithConfig = nextConfig => {
    setLocalFilters(prev => {
      let changed = false;
      const next = { ...prev };

      nextConfig.forEach(filter => {
        const key = filter.key;
        const selected = prev[key];
        const options = filter.options || [];

        if (!selected || selected.length === 0) return;

        const validValues = new Set(options.map(o => JSON.stringify(o.fullValue)));
        const validSelected = selected.filter(s => validValues.has(JSON.stringify(s.fullValue)));

        if (validSelected.length !== selected.length) {
          changed = true;
          if (validSelected.length > 0) {
            next[key] = validSelected;
          } else {
            delete next[key];
          }
        }
      });
      console.log('Reconciled local filters :', next,prev);
      return changed ? next : prev;
    });
  };

  const handleConfig = (allSectionsConfig, dynamicFilterParams = {}) => {
    const queryFilters = parseQueryParams(location.search);
    const applied_filter_values = filterSlice.applied_filter_values;

    const hasQueryFilters = queryFilters && Object.keys(queryFilters).length > 0;
    const activeFilters = hasQueryFilters ? queryFilters : {};

    let mergedDefaultFilters = {};
    let mergedFiltersWithLabels = {};

    Object.keys(allSectionsConfig).forEach(section => {
      const filtersConfig = allSectionsConfig[section] || [];
      let defaultFilters = {};

      filtersConfig.forEach(filter => {
        const { key, defaultValue } = filter;

        if (activeFilters[key]) {
          defaultFilters[key] = activeFilters[key];
        } else if (applied_filter_values[key]) {
          defaultFilters[key] = applied_filter_values[key];
        } else if (defaultValue) {
          defaultFilters[key] = [defaultValue];
        }
      });

      mergedDefaultFilters = {
        ...mergedDefaultFilters,
        ...defaultFilters
      };

      mergedFiltersWithLabels = {
        ...mergedFiltersWithLabels,
        ...updateFilterValues(defaultFilters, filtersConfig)
      };
    });

    if (dynamicFilterParams && Object.keys(dynamicFilterParams).length > 0) {
      fetchDynamicFilters(
        dynamicFilterParams,
        mergedDefaultFilters?.timePeriod,
        mergedDefaultFilters?.property_id,
        allSectionsConfig
      );
    }

    dispatch({
      type: filter.SET_APPLIED_FILTER_VALUES,
      payload: { applied_filter_values: mergedDefaultFilters }
    });

    dispatch({
      type: filter.SET_APPLIED_FILTERS,
      payload: { applied_filters: mergedFiltersWithLabels }
    });

    if (!localFilters || Object.keys(localFilters).length === 0) setLocalFilters(mergedFiltersWithLabels);

    dispatch(fetchDataWithAppliedFilters(mergedDefaultFilters, currentSection, allSectionsConfig[currentSection]));

    console.log('dynamicFilterParams', dynamicFilterParams, mergedFiltersWithLabels);
  };

  useEffect(() => {
    const config = filterSlice.config?.[currentSection];
    if (!config) return;

    const queryFilters = parseQueryParams(location.search);

    const filtersWithLabel = updateFilterValues(queryFilters, config);

    dispatch({
      type: filter.SET_APPLIED_FILTERS,
      payload: { applied_filters: filtersWithLabel }
    });

    dispatch({
      type: filter.SET_APPLIED_FILTER_VALUES,
      payload: { applied_filter_values: queryFilters }
    });

    setLocalFilters(filtersWithLabel);
  }, [location.search]);

  const fetchDynamicFilters = (dynamicFilterParams, currentTimePeriod, currentPropertyId, allSectionsConfig) => {
    console.log('Fetching dynamic filters with params :', dynamicFilterParams, currentTimePeriod);
    const { start_date, end_date } = currentTimePeriod?.[0];
    dispatch({
      type: filter.SET_DYNAMIC_FILTER_VALUES_LOADING,
      payload: { loading: true }
    });
    if (!start_date || !end_date) {
      dispatch({
        type: filter.SET_DYNAMIC_FILTER_VALUES_LOADING,
        payload: { loading: false }
      });
      return;
    }
    const start = new Date(start_date);
    const end = new Date(end_date);
    const diffMonths = (end - start) / (1000 * 60 * 60 * 24 * 30);
    const batches = [];
    if (diffMonths <= 6) {
      batches.push({ start_date: start_date, end_date: end_date });
    } else {
      let currentStart = new Date(start);
      while (currentStart < end) {
        let currentEnd = new Date(currentStart);
        currentEnd.setMonth(currentEnd.getMonth() + 6);
        if (currentEnd > end) currentEnd = end;
        batches.push({
          start_date: currentStart.toISOString().split('T')[0],
          end_date: currentEnd.toISOString().split('T')[0]
        });
        currentStart = currentEnd;
      }
    }
    const promises = batches.map(
      batch =>
        new Promise(resolve => {
          dispatch(
            getGeneric({
              requestBody: {
                data_tag: 'DATATAG_DELIGHTIQ_CST_FILTERS',
                request_data: {
                  ...dynamicFilterParams,
                  filters: { ...batch, additional_filters: { property_id: currentPropertyId } }
                }
              },
              onSuccess: e => {
                console.log('Dyanamic filter response for batch ', batch, e);
                const cst = get(e, 'api_response.METHODTAG_DELIGHTIQ_PARSE_TO_FILTER_VALUES.data', {});
                resolve(cst);
              },
              onError: err => {
                console.log(err);
                resolve({});
              }
            })
          );
        })
    );
    Promise.all(promises).then(results => {
      console.log('Dynamic filter results :', results);

      const merged = results.reduce((acc, curr) => {
        Object.keys(curr).forEach(key => {
          const mergedOptions = [...(acc[key] || []), ...(curr[key] || [])];

          acc[key] = preProcessOptions(mergedOptions);
        });
        return acc;
      }, {});

      updateConfigWithFilters(merged, allSectionsConfig);
    });
  };

  const updateConfigWithFilters = (cstFiltersValues, allSectionsConfig) => {
    Object.keys(allSectionsConfig).forEach(section => {
      const filters = allSectionsConfig[section].map(filter =>
        cstFiltersValues[filter.key] ? { ...filter, options: cstFiltersValues[filter.key] } : filter
      );

      dispatch({
        type: filter.SET_CONFIG,
        payload: { section, config: filters }
      });
      if (section === currentSection) {
        reconcileLocalFiltersWithConfig(filters);
      }
    });
    dispatch({
      type: filter.SET_DYNAMIC_FILTER_VALUES_LOADING,
      payload: { loading: false }
    });
  };

  useEffect(() => {
    if (currentSection === 'default') return;
    dispatch({
      type: filter.SET_FILTER_LOADING,
      payload: { loading: true }
    });
    const campaign = currentSection;
    const config = get(filterSlice, `config`, null);
    if (config && Object.keys(config).length > 0) {
      dispatch({
        type: filter.SET_FILTER_LOADING,
        payload: { loading: false }
      });
      handleConfig(config);
      return;
    }
    dispatch(
      getCampaignWidgets({
        activeHotelId,
        campaign: 'filter_common',
        pageType: 'filter',
        onSuccess: configResponse => {
          const filtersConfig = get(configResponse, 'widget_list[0].contents.filter_config', {});
          const sections = get(configResponse, 'widget_list[0].contents.sections', []);
          const dynamicFilterParams = get(configResponse, 'widget_list[0].contents.dynamic_filter_params', {});
          dispatch({
            type: filter.SET_DYNAMIC_FILTER_PARAMS,
            payload: { keys: dynamicFilterParams }
          });
          const currentSectionFiltersConfig = get(filtersConfig, currentSection, []);
          console.log(
            currentSectionFiltersConfig,
            filtersConfig,
            sections,
            dynamicFilterParams,
            'currentSectionFiltersConfig'
          );
          currentSectionFiltersConfig.forEach(filter => {
            filter.options = preProcessOptions(filter.options || []);
          });

          const allSectionsConfig = Object.keys(filtersConfig).reduce((acc, section) => {
            acc[section] = (filtersConfig[section] || []).map(filter => ({
              ...filter,
              options: preProcessOptions(filter.options || [])
            }));
            return acc;
          }, {});

          sections.forEach(section => {
            const filtersForSection = get(filtersConfig, section, []);
            filtersForSection.forEach(filter => {
              filter.options = preProcessOptions(filter.options || []);
            });
            dispatch({
              type: filter.SET_CONFIG,
              payload: { section: section, config: filtersForSection }
            });
          });
          dispatch({
            type: filter.SET_FILTER_LOADING,
            payload: { loading: false }
          });
          handleConfig(allSectionsConfig, dynamicFilterParams);
        },
        onError: error => {
          dispatch({
            type: filter.SET_FILTER_LOADING,
            payload: { loading: false }
          });
          dispatch({
            type: filter.SET_FILTER_ERROR
          });
        }
      })
    );
  }, [currentSection]);

  const handleRun = () => {
    const appliedFilterValues = normalize(localFilters);
    dispatch({
      type: filter.SET_APPLIED_FILTER_VALUES,
      payload: { applied_filter_values: appliedFilterValues }
    });
    const stringified = encodeURIComponent(JSON.stringify(appliedFilterValues));
    const parsedQuery = qs.parse(location.search);
    parsedQuery.filters = stringified;

    const newQuery = qs.stringify(parsedQuery);
    history.push(`${location.pathname}?${newQuery}`);

    dispatch({
      type: filter.SET_APPLIED_FILTERS,
      payload: { applied_filters: localFilters }
    });
    dispatch(fetchDataWithAppliedFilters(appliedFilterValues, currentSection, filterSlice?.config[currentSection]));
    setOpen(false);
  };

  const debouncedFetchDynamicFilters = React.useRef(
    debounce((filters, dynamicFilterParams, config) => {
      const timePeriod = filters?.timePeriod?.map(t => t?.fullValue);
      const propertyIds = filters?.property_id?.map(item => item?.fullValue) || [];
      fetchDynamicFilters(dynamicFilterParams, timePeriod, propertyIds, config);
    })
  ).current;

  const handleFilterChange = nextFilters => {
    setLocalFilters(nextFilters);
    const timeChanged = !isSameValue(localFilters?.timePeriod, nextFilters?.timePeriod);
    const propertyChanged = !isSameValue(localFilters?.property_id, nextFilters?.property_id);
    if (timeChanged || propertyChanged) {
      debouncedFetchDynamicFilters(nextFilters, filterSlice.dynamicFilterParams, filterSlice.config);
    }
  };

  if (currentSection === 'default') {
    return <></>;
  }
  return (
    <View style={!open && { position: 'sticky', zIndex: 1000, top: 76 }}>
      <FilterBar
        key={JSON.stringify(filterSlice.config?.[currentSection]?.map(f => f.options?.length))}
        filters={localFilters}
        appliedFilters={applied_filters}
        onFiltersChange={handleFilterChange}
        onRun={handleRun}
        showLoading={showLoading}
        config={get(filterSlice, `config.${currentSection}`, [])}
        open={open}
        setOpen={setOpen}
      />
    </View>
  );
};

const mapStateToProps = state => ({
  activeHotelId: state.app?.active_hotel_id,
  language: state.app?.language,
  allMessageFetched: state.app?.allMessageFetched,
  filterSlice: state.filterReducer
});

export default connect(mapStateToProps)(FilterBarContainer);
